import { IconEdit, IconTrash } from "@tabler/icons-react";
import { bankAccountApi } from "apis/bankAccountApi";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomTable from "components/ui/CustomTable";
import CustomText from "components/ui/CustomText/CustomText";
import GroupActions from "components/ui/GroupActions";
import { ActionItem } from "components/ui/GroupActions/GroupActions";
import { STATUS } from "constants/common/common";
import { CommonParamsType } from "constants/types/common";
import { BankAccountResponseType } from "constants/types/bankAccount";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "mantine-react-table";
import { FORM_IDS } from "pages/BankAccount/config";
import { FC, useMemo, useRef, useCallback } from "react";
import { useNotification } from "utils/hooks";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import { useSWRConfig } from "swr";
import { Loading } from "components";
import EditForm from "../EditForm/EditForm";
import { getBankAccountListKey, useBankAccounts } from "utils/hooks/request/useBankAccount";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";

interface BankAccountTableProps {
  params: CommonParamsType;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const ICON_SIZE = 18;

const BankAccountTable: FC<BankAccountTableProps> = ({
  params,
  onPageChange,
  onLimitChange,
}) => {
  const { notification } = useNotification();
  const { mutate } = useSWRConfig();
  const ability = useAbility();

  const {
    bankAccounts,
    pagination,
    isLoading,
    mutate: localMutate,
  } = useBankAccounts(params);

  const { openCrudModal, closeAll, closeCrudModal } = useAppModal();
  const activeModalId = useRef<string | null>(null);

  const handleCloseModal = useCallback(() => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  }, [closeCrudModal]);

  const handleDeleteBankAccount = useCallback(
    async (id: string) => {
      try {
        await bankAccountApi.delete(id);
        notification.success({ title: "Xóa tài khoản ngân hàng thành công" });
        closeAll();
        localMutate();
        onPageChange(1);
      } catch {
        notification.error({ title: "Xóa tài khoản ngân hàng thất bại" });
      }
    },
    [notification, closeAll, localMutate, onPageChange],
  );

  const handleOpenDeleteModal = useCallback(
    (id: string) => {
      openCrudModal(
        <CustomText variant="section-title">Xác nhận xóa tài khoản ngân hàng</CustomText>,
        {
          mode: "delete",
          formId: FORM_IDS.DELETE,
          onConfirmDelete: async () => await handleDeleteBankAccount(id),
        },
      );
    },
    [openCrudModal, handleDeleteBankAccount],
  );

  const handleOpenEditModal = useCallback(
    (bankAccount: BankAccountResponseType) => {
      const modalId = openCrudModal(
        <CustomText variant="section-title">Chỉnh sửa tài khoản ngân hàng</CustomText>,
        {
          mode: "edit",
          formId: FORM_IDS.EDIT,
          children: (
            <EditForm
              bankAccount={bankAccount}
              mutate={() => mutate(getBankAccountListKey(params))}
              onClose={handleCloseModal}
            />
          ),
          submitText: "Cập nhật",
        },
        500,
      );
      activeModalId.current = modalId;
    },
    [openCrudModal, mutate, params, handleCloseModal],
  );

  const bankAccountActions: ActionItem<BankAccountResponseType>[] = useMemo(
    () => [
      {
        key: "edit",
        label: "Chỉnh sửa",
        icon: <IconEdit size={ICON_SIZE} />,
        color: "orange",
        permission: "bank_account:update",
        onClick: (row) => handleOpenEditModal(row),
      },
      {
        key: "delete",
        label: "Xóa",
        icon: <IconTrash size={ICON_SIZE} />,
        color: "red",
        permission: "bank_account:delete",
        onClick: (row) => handleOpenDeleteModal(row.id),
      },
    ],
    [handleOpenEditModal, handleOpenDeleteModal],
  );

  const hasActions = useMemo(
    () => bankAccountActions.some((a) => canAccessRequirement(ability, a.permission, true)),
    [bankAccountActions, ability],
  );

  const columns: MRT_ColumnDef<BankAccountResponseType>[] = [
    {
      id: "stt",
      header: "STT",
      size: 5,
      grow: false,
      Cell: ({ row }) => row.index + 1,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "code",
      header: "Mã tài khoản",
    },
    {
      accessorKey: "bankName",
      header: "Tên ngân hàng",
    },
    {
      accessorKey: "accountNumber",
      header: "Số tài khoản",
    },
    {
      accessorKey: "accountHolder",
      header: "Chủ tài khoản",
    },
    {
      accessorKey: "isActive",
      header: "Trạng thái",
      grow: false,
      Cell: ({ cell }) =>
        cell.getValue() ? (
          <CustomBadge label={STATUS.ACTIVE_LABEL} color="green" />
        ) : (
          <CustomBadge label={STATUS.INACTIVE_LABEL} color="gray" />
        ),
    },
    ...(hasActions
      ? [
          {
            id: "actions",
            header: "Thao tác",
            enableSorting: false,
            enableColumnFilter: false,
            size: 50,
            grow: false,
            enableResizing: false,
            mantineTableHeadCellProps: { align: "center" },
            mantineTableBodyCellProps: { align: "center" },
            Cell: ({ row }: any) => (
              <GroupActions row={row.original} actions={bankAccountActions} />
            ),
          } satisfies MRT_ColumnDef<BankAccountResponseType>,
        ]
      : []),
  ];

  const handleChange = useCallback(
    (state: {
      page: number;
      limit: number;
      filters?: MRT_ColumnFiltersState;
    }) => {
      if (pagination && state.page !== pagination.page) {
        onPageChange(state.page);
      } else if (pagination && state.limit !== pagination.limit) {
        onLimitChange(state.limit);
      }
    },
    [onPageChange, onLimitChange, pagination],
  );

  return (
    <Loading variant="overlay" visible={isLoading}>
      <CustomTable
        columns={columns}
        data={bankAccounts ?? []}
        pagination={pagination!}
        onStateChange={handleChange}
      />
    </Loading>
  );
};

export default BankAccountTable;
